import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CartDrawer from "@/components/CartDrawer";
import StatusBadge from "@/components/StatusBadge";
import WishlistButton from "@/components/WishlistButton";
import { resolveUrl } from "@/components/ImageUploader";
import BrandLogo from "@/components/BrandLogo";
import StorefrontHomeEditorial from "@/pages/StorefrontHomeEditorial";
import StorefrontHomeEditorialLuxe from "@/pages/StorefrontHomeEditorialLuxe";
import BrandStyle from "@/components/BrandStyle";
import { ArrowRight, ShoppingBag, Mail, Heart, User, Instagram, Facebook, Linkedin, Youtube, Twitter } from "lucide-react";
import { toast } from "sonner";

export default function StorefrontHomeWrapper() {
  const { slug } = useParams();
  return (
    <CartProvider slug={slug}>
      <StorefrontHomeBody slug={slug} />
      <CartDrawer />
    </CartProvider>
  );
}

function StorefrontHomeBody({ slug }) {
  const { t: tNs } = useI18n();
  const [data, setData] = useState(null);
  const [cms, setCms] = useState(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/storefront/${slug}`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/public/cms/${slug}/home`).then((r) => setCms(r.data)).catch(() => {});
    axios.get(`${API}/public/storefront/${slug}/best-sellers?limit=4`).then((r) => setBestSellers(r.data)).catch(() => {});
  }, [slug]);

  const subscribeNewsletter = async (e) => {
    e?.preventDefault();
    try {
      await axios.post(`${API}/public/newsletter/subscribe`, { email: newsletterEmail, tenant_slug: slug });
      toast.success(tNs("store_newsletter_success"));
      setNewsletterEmail("");
    } catch (err) { toast.error(err.response?.data?.detail || tNs("error")); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{tNs("loading")}</div>;

  const template = data.tenant?.settings?.brand_kit?.template || "minimal";
  if (template === "editorial-luxe") {
    return <StorefrontHomeEditorialLuxe data={data} cms={cms} bestSellers={bestSellers} newsletterEmail={newsletterEmail} setNewsletterEmail={setNewsletterEmail} subscribeNewsletter={subscribeNewsletter} />;
  }
  if (template === "editorial") {
    return <StorefrontHomeEditorial data={data} cms={cms} bestSellers={bestSellers} newsletterEmail={newsletterEmail} setNewsletterEmail={setNewsletterEmail} subscribeNewsletter={subscribeNewsletter} />;
  }
  return <MinimalHome slug={slug} data={data} cms={cms} bestSellers={bestSellers} newsletterEmail={newsletterEmail} setNewsletterEmail={setNewsletterEmail} subscribeNewsletter={subscribeNewsletter} />;
}

function MinimalHome({ slug, data, cms, bestSellers, newsletterEmail, setNewsletterEmail, subscribeNewsletter }) {
  const cart = useCart();
  const { user } = useAuth();
  const { t } = useI18n();
  const { tenant, network, items } = data;
  const statuses = network.vertical_config?.availability_statuses || [];
  const brand = tenant.settings?.brand_kit || {};
  const social = tenant.settings?.social_links || {};
  const primaryColor = brand.primary_color || "#0A0A0A";
  const secondaryColor = brand.secondary_color || "#C25934";
  const [category, setCategory] = useState(null);
  const categories = network.vertical_config?.categories || [];
  const filtered = category ? items.filter((i) => i.categoria === category) : items;
  const heroTitle = cms?.sections?.find((s) => s.type === "hero")?.content?.title || tenant.name;
  const heroSubtitle = cms?.sections?.find((s) => s.type === "hero")?.content?.subtitle || brand.tagline || "";
  const heroBg = cms?.sections?.find((s) => s.type === "hero")?.content?.background_image;

  return (
    <div className="min-h-screen bg-white" data-testid="storefront-home" data-storefront="true" style={{ "--brand-primary": primaryColor, "--brand-secondary": secondaryColor }}>
      <BrandStyle brand={brand} />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <BrandLogo
            logoUrl={brand.logo_url}
            fallbackText={tenant.name}
            resolveUrl={resolveUrl}
            className="h-9"
            dataTestid="storefront-name"
          />
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to={`/s/${slug}/search`} className="w-9 h-9 rounded-sm border border-border hover:bg-accent flex items-center justify-center" data-testid="header-search-link"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg></Link>
            <Link to={user?.role === "customer" ? "/account" : "/login"} className="w-9 h-9 rounded-sm border border-border hover:bg-accent flex items-center justify-center" data-testid="header-account"><User className="w-4 h-4" /></Link>
            <Link to={user?.role === "customer" ? "/account" : "/login"} className="w-9 h-9 rounded-sm border border-border hover:bg-accent flex items-center justify-center" data-testid="header-wishlist"><Heart className="w-4 h-4" /></Link>
            <button data-testid="header-cart" onClick={() => cart.setOpen(true)} className="relative inline-flex items-center gap-1.5 px-3 h-9 rounded-sm border border-border hover:bg-accent">
              <ShoppingBag className="w-4 h-4" />
              {cart.count > 0 && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-[10px] font-bold text-white flex items-center justify-center bg-brand">{cart.count}</span>}
            </button>
          </div>
        </div>
      </header>

      <section className="border-b border-border" style={{ background: heroBg ? "transparent" : `linear-gradient(135deg, ${primaryColor}10 0%, transparent 100%)` }}>
        <div className="relative">
          {heroBg && (<div className="absolute inset-0"><img src={resolveUrl(heroBg)} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/30" /></div>)}
          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
            <div className="overline mb-4" style={{ color: heroBg ? "#fff" : secondaryColor }}>{network.display_name}</div>
            <h1 className={`editorial-h1 text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] mb-5 max-w-4xl ${heroBg ? "text-white" : ""}`}>{heroTitle}</h1>
            {heroSubtitle && <p className={`text-xl max-w-2xl ${heroBg ? "text-white/90" : "text-muted-foreground"}`}>{heroSubtitle}</p>}
          </div>
        </div>
      </section>

      <section id="products" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          {categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-8">
              <button data-testid="cat-filter-all" onClick={() => setCategory(null)} className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full ${!category ? "bg-foreground text-white" : "border border-border text-muted-foreground"}`}>{t("store_all")}</button>
              {categories.map((cc) => (
                <button key={cc} data-testid={`cat-filter-${cc}`} onClick={() => setCategory(cc)} className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full whitespace-nowrap ${category === cc ? "bg-foreground text-white" : "border border-border text-muted-foreground"}`}>{cc}</button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {filtered.map((item) => (
              <div key={item.sku_variante} className="group relative" data-testid={`product-card-${item.sku_variante}`}>
                <Link to={`/s/${slug}/style/${item.sku_madre || item.sku_variante}`}>
                  <div className="relative overflow-hidden bg-neutral-100 aspect-[4/5] mb-3">
                    {item.immagini?.[0] ? (
                      <img src={resolveUrl(item.immagini[0])} alt={item.nome} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground/40">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M21 15l-5-5L5 21" />
                        </svg>
                        <span className="text-[10px] uppercase tracking-wider mt-2 opacity-70">Foto in arrivo</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3"><StatusBadge status={item.stato_disponibilita} statuses={statuses} /></div>
                    {item.sconto_percentuale > 0 && <div className="absolute top-3 right-12 px-2.5 py-1 bg-foreground text-white text-xs font-bold rounded-full">-{item.sconto_percentuale}%</div>}
                  </div>
                </Link>
                <div className="absolute top-3 right-3"><WishlistButton sku={item.sku_variante} tenantSlug={slug} size="sm" /></div>
                <div className="overline text-muted-foreground mb-1">{item.categoria}</div>
                <Link to={`/s/${slug}/style/${item.sku_madre || item.sku_variante}`}><h3 className="font-heading font-bold text-xl mb-1 leading-tight">{item.nome}</h3></Link>
                {/* Caratteristiche essenziali */}
                <ProductCardFeatures item={item} />
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading font-bold text-xl">€ {item.prezzo_finale}</span>
                    {item.sconto_percentuale > 0 && <span className="text-sm text-muted-foreground line-through">€ {item.prezzo_originale}</span>}
                  </div>
                  <button onClick={() => cart.add(item.sku_variante, 1, { nome: item.nome, image: item.immagini?.[0], custom_attributes: item.custom_attributes_variante })}
                    data-testid={`add-to-cart-${item.sku_variante}`}
                    className="text-xs px-3 h-9 rounded-sm border border-border hover:bg-brand hover:text-white hover:border-brand transition-colors">
                    + {t("nav_cart")}
                  </button>
                </div>
                {item.stock_disponibile > 0 ? (
                  <div className="text-[11px] text-emerald-700 mt-2" data-testid={`stock-${item.sku_variante}`}>● {item.stock_disponibile} {item.stock_disponibile === 1 ? t("store_in_stock_one") : t("store_in_stock_many")}</div>
                ) : (
                  <div className="text-[11px] text-amber-700 mt-2" data-testid={`stock-${item.sku_variante}`}>● {t("store_preorder")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border" style={{ background: `${primaryColor}10` }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 text-center">
          <Mail className="w-8 h-8 mx-auto mb-4" style={{ color: secondaryColor }} />
          <h2 className="font-heading font-bold text-3xl mb-3">Resta aggiornato</h2>
          <form onSubmit={subscribeNewsletter} className="flex gap-2 max-w-md mx-auto">
            <input data-testid="newsletter-email" type="email" required placeholder="la-tua@email.com" value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} className="flex-1 h-12 px-4 rounded-sm border border-border text-sm" />
            <button data-testid="newsletter-submit" className="px-5 h-12 rounded-sm text-white text-sm font-medium" style={{ background: secondaryColor }}>Iscriviti</button>
          </form>
        </div>
      </section>

      <footer className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-heading font-bold text-lg mb-2">{tenant.name}</div>
            {brand.tagline && <p className="text-sm text-white/70">{brand.tagline}</p>}
          </div>
          <div>
            <div className="overline text-white/60 mb-3">Legal</div>
            <Link to={`/s/${slug}/legal/privacy`} className="block text-sm text-white/80 hover:text-white">Privacy</Link>
            <Link to={`/s/${slug}/legal/cookie`} className="block text-sm text-white/80 hover:text-white">Cookie</Link>
            <Link to={`/s/${slug}/legal/terms`} className="block text-sm text-white/80 hover:text-white">{t("store_terms_conditions")}</Link>
          </div>
          <div>
            <div className="overline text-white/60 mb-3">{t("store_follow_us")}</div>
            <div className="flex gap-2">{Object.entries(social).filter(([, v]) => v).map(([net, url]) => { const I = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, youtube: Youtube, x_twitter: Twitter, tiktok: Twitter }[net] || Twitter; return <a key={net} href={url} target="_blank" rel="noreferrer" className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20"><I className="w-4 h-4" /></a>; })}</div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between text-xs text-white/50">
            <span>© 2026 {tenant.name}</span>
            <a href="/" className="hover:text-white">Powered by TotalKlick</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ProductCardFeatures({ item }) {
  const dim = item?.dimensioni || {};
  const mat = Array.isArray(item?.materiali) ? item.materiali : [];
  const fv = item?.feature_visibility || {};
  const showDim = fv.dimensioni !== false;
  const showMat = fv.materiali !== false;
  const dimLine = [];
  if (showDim) {
    if (dim.larghezza_cm) dimLine.push(`L ${dim.larghezza_cm}`);
    if (dim.profondita_cm) dimLine.push(`P ${dim.profondita_cm}`);
    if (dim.altezza_cm) dimLine.push(`H ${dim.altezza_cm}`);
    if (!dimLine.length && dim.diametro_cm) dimLine.push(`Ø ${dim.diametro_cm}`);
  }
  const matLine = showMat ? mat.slice(0, 2).join(" · ") : "";
  if (!dimLine.length && !matLine) return null;
  return (
    <div className="text-[11px] text-muted-foreground space-y-0.5 mt-0.5" data-testid="card-features">
      {dimLine.length > 0 && <div>{dimLine.join(" × ")} <span className="opacity-60">cm</span></div>}
      {matLine && <div className="truncate">{matLine}</div>}
    </div>
  );
}

